/**
 * Section Animation System
 * 为所有 Section 提供可配置的入场动画效果
 * 支持多种动画类型和滚动触发
 */

class SectionAnimationController {
  constructor() {
    this.animatedSections = new Set();
    this.observers = new Map();
    this.init();
  }

  /**
   * 初始化动画系统
   */
  init() {
    // 检查用户是否偏好减少动画
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.disableAllAnimations();
      return;
    }

    // 初始化所有动画元素
    this.initializeAnimations();

    // 监听 Shopify 主题编辑器事件
    if (window.Shopify && Shopify.designMode) {
      this.setupDesignModeListeners();
    }
  }

  /**
   * 初始化所有动画元素
   */
  initializeAnimations(rootEl = document) {
    const animationElements = rootEl.querySelectorAll('.section-animation');

    animationElements.forEach((element) => {
      const triggerType = element.dataset.animationTrigger || 'scroll';

      switch (triggerType) {
        case 'scroll':
          this.setupScrollAnimation(element);
          break;
        case 'immediate':
          this.triggerAnimation(element);
          break;
        case 'delay':
          this.setupDelayedAnimation(element);
          break;
        default:
          this.setupScrollAnimation(element);
      }
    });
  }

  /**
   * 设置滚动触发动画
   */
  setupScrollAnimation(element) {
    // 获取触发阈值配置
    const threshold = parseFloat(element.dataset.animationThreshold || '0.1');
    const rootMargin = element.dataset.animationRootMargin || '0px 0px -50px 0px';

    // 创建 Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.triggerAnimation(entry.target);
            // 动画触发后取消观察（除非设置了重复播放）
            if (!entry.target.dataset.animationRepeat) {
              observer.unobserve(entry.target);
            }
          } else if (entry.target.dataset.animationRepeat) {
            // 如果设置了重复播放，元素离开视口时重置动画
            this.resetAnimation(entry.target);
          }
        });
      },
      {
        threshold: threshold,
        rootMargin: rootMargin,
      }
    );

    observer.observe(element);
    this.observers.set(element, observer);
  }

  /**
   * 设置延迟触发动画
   */
  setupDelayedAnimation(element) {
    const delay = parseInt(element.dataset.animationDelayMs || '0', 10);

    setTimeout(() => {
      this.triggerAnimation(element);
    }, delay);
  }

  /**
   * 触发动画
   */
  triggerAnimation(element) {
    // 如果已经动画过且不重复，跳过
    if (this.animatedSections.has(element) && !element.dataset.animationRepeat) {
      return;
    }

    // 处理级联动画
    if (element.dataset.animationCascade === 'true') {
      this.triggerCascadeAnimation(element);
    } else {
      this.playAnimation(element);
    }

    this.animatedSections.add(element);
  }

  /**
   * 播放动画
   */
  playAnimation(element, index = 0) {
    // 设置级联序号
    if (index > 0) {
      element.style.setProperty('--animation-order', index);
    }

    // 添加动画类
    requestAnimationFrame(() => {
      element.classList.add('animated');
      element.classList.add('animate-on-scroll');
    });

    // 动画完成后的回调
    const duration = this.getAnimationDuration(element);
    const delay = this.getAnimationDelay(element, index);

    setTimeout(() => {
      this.onAnimationComplete(element);
    }, duration + delay);
  }

  /**
   * 触发级联动画
   */
  triggerCascadeAnimation(element) {
    const cascadeSelector = element.dataset.animationCascadeSelector || '.section-animation-item';
    const cascadeItems = element.querySelectorAll(cascadeSelector);

    if (cascadeItems.length === 0) {
      this.playAnimation(element);
      return;
    }

    cascadeItems.forEach((item, index) => {
      item.classList.add('section-animation');

      // 复制动画配置到子元素
      const animationType = element.dataset.animationType;
      if (animationType) {
        item.classList.add(`animate-${animationType}`);
      }

      // 设置级联延迟
      item.setAttribute('data-cascade', 'true');

      this.playAnimation(item, index);
    });
  }

  /**
   * 重置动画
   */
  resetAnimation(element) {
    element.classList.remove('animated');
    element.classList.remove('animate-on-scroll');

    // 强制重排以重置动画
    void element.offsetWidth;

    this.animatedSections.delete(element);
  }

  /**
   * 动画完成回调
   */
  onAnimationComplete(element) {
    // 触发自定义事件
    const event = new CustomEvent('section-animation-complete', {
      detail: { element: element },
      bubbles: true,
    });
    element.dispatchEvent(event);

    // 如果设置了自动重置，延迟后重置动画
    const autoReset = element.dataset.animationAutoReset;
    if (autoReset) {
      const resetDelay = parseInt(autoReset, 10);
      setTimeout(() => {
        this.resetAnimation(element);
      }, resetDelay);
    }
  }

  /**
   * 获取动画持续时间（毫秒）
   */
  getAnimationDuration(element) {
    const computed = window.getComputedStyle(element);
    const duration = computed.getPropertyValue('--animation-duration') || '0.6s';
    return parseFloat(duration) * 1000;
  }

  /**
   * 获取动画延迟时间（毫秒）
   */
  getAnimationDelay(element, cascadeIndex = 0) {
    const computed = window.getComputedStyle(element);
    const delay = computed.getPropertyValue('--animation-delay') || '0s';
    const baseDelay = parseFloat(delay) * 1000;

    // 计算级联延迟
    const cascadeDelay = cascadeIndex * 100; // 每个元素延迟 100ms

    return baseDelay + cascadeDelay;
  }

  /**
   * 禁用所有动画
   */
  disableAllAnimations() {
    const animationElements = document.querySelectorAll('.section-animation');
    animationElements.forEach((element) => {
      element.classList.add('animated');
      element.style.opacity = '1';
      element.style.transform = 'none';
    });
  }

  /**
   * 设置主题编辑器监听器
   */
  setupDesignModeListeners() {
    // Section 加载时重新初始化
    document.addEventListener('shopify:section:load', (event) => {
      this.initializeAnimations(event.target);
    });

    // Section 重新排序时重新初始化
    document.addEventListener('shopify:section:reorder', () => {
      this.cleanup();
      this.initializeAnimations();
    });

    // Section 卸载时清理
    document.addEventListener('shopify:section:unload', (event) => {
      this.cleanupSection(event.target);
    });
  }

  /**
   * 清理单个 Section
   */
  cleanupSection(section) {
    const animationElements = section.querySelectorAll('.section-animation');
    animationElements.forEach((element) => {
      const observer = this.observers.get(element);
      if (observer) {
        observer.disconnect();
        this.observers.delete(element);
      }
      this.animatedSections.delete(element);
    });
  }

  /**
   * 清理所有观察器
   */
  cleanup() {
    this.observers.forEach((observer) => {
      observer.disconnect();
    });
    this.observers.clear();
    this.animatedSections.clear();
  }

  /**
   * 手动触发指定元素的动画
   */
  manualTrigger(selector) {
    const element = document.querySelector(selector);
    if (element) {
      this.triggerAnimation(element);
    }
  }

  /**
   * 重新初始化指定元素的动画
   */
  reinitialize(selector) {
    const element = document.querySelector(selector);
    if (element) {
      this.resetAnimation(element);
      this.setupScrollAnimation(element);
    }
  }
}

// 创建全局实例
let sectionAnimationController = null;

// DOM 加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    sectionAnimationController = new SectionAnimationController();
  });
} else {
  sectionAnimationController = new SectionAnimationController();
}

// 导出到全局作用域，方便其他脚本调用
window.SectionAnimationController = SectionAnimationController;
window.sectionAnimationController = sectionAnimationController;
